import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { cardExpiry, cardTitle, ExtensionCardOption, NEW_CARD_CHOICE } from './extensionPayment';

const List = styled.div`
  ${tw`flex flex-col gap-2 my-3 w-full`}
`;

const Legend = styled.p`
  ${tw`text-sm font-semibold mb-1`}
  color: var(--color-text-primary);
`;

const Row = styled.label<{ $selected: boolean; $disabled: boolean }>`
  ${tw`flex items-center gap-3 rounded-md px-3 py-2 text-sm`}
  border: 1px solid
    ${({ $selected }) => ($selected ? 'var(--color-black)' : 'var(--color-border)')};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
`;

const RowText = styled.span`
  ${tw`flex flex-col`}
`;

const RowTitle = styled.span`
  color: var(--color-text-primary);
`;

const RowMeta = styled.span`
  ${tw`text-xs`}
  color: var(--color-text-secondary);
`;

interface ExtensionCardPickerProps {
  cards: ExtensionCardOption[];
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Spec 013 P3: the guest picks the card for an extension. The booking's own cards come
 * first, then cards saved on this site, then "Use a new card".
 */
export const ExtensionCardPicker: React.FC<ExtensionCardPickerProps> = ({
  cards,
  value,
  onChange,
  disabled = false,
}) => (
  <List role="radiogroup" aria-label="Card for this extension">
    <Legend>Pay with</Legend>
    {cards.map(card => {
      const rowDisabled = disabled || card.isExpired;
      return (
        <Row key={card.id} $selected={value === card.id} $disabled={rowDisabled}>
          <input
            type="radio"
            name="extension-card"
            value={card.id}
            checked={value === card.id}
            disabled={rowDisabled}
            onChange={() => onChange(card.id)}
          />
          <RowText>
            <RowTitle>{cardTitle(card)}</RowTitle>
            <RowMeta>
              {cardExpiry(card)}
              {card.usedByThisBooking ? ' · Used for this trip' : ''}
            </RowMeta>
          </RowText>
        </Row>
      );
    })}
    <Row $selected={value === NEW_CARD_CHOICE} $disabled={disabled}>
      <input
        type="radio"
        name="extension-card"
        value={NEW_CARD_CHOICE}
        checked={value === NEW_CARD_CHOICE}
        disabled={disabled}
        onChange={() => onChange(NEW_CARD_CHOICE)}
      />
      <RowText>
        <RowTitle>Use a new card</RowTitle>
        <RowMeta>The card must be in your own name.</RowMeta>
      </RowText>
    </Row>
  </List>
);
